import type { ItpDataGridColumn } from './types';

export type ColumnValueReader = (row: any) => any;

export type ColumnValueLookup = (row: any, column: ItpDataGridColumn) => any;

export function compilePath(path: string): ColumnValueReader {
  const walked = path.replace(/\[(\w+)\]/g, '.$1');
  if (!walked.includes('.')) {
    return (row) => row?.[walked];
  }
  const segments = walked.split('.');
  return (row) => {
    if (row == null) {
      return undefined;
    }
    if (typeof row === 'object' && path in row) {
      return row[path];
    }
    let current: any = row;
    for (const segment of segments) {
      if (current == null) {
        return undefined;
      }
      current = current[segment];
    }
    return current;
  };
}

export function columnValueReader(
  column: ItpDataGridColumn,
): ColumnValueReader {
  const getter = column.valueGetter;
  if (typeof getter === 'function') {
    return (row) => getter(row, column);
  }
  if (typeof getter === 'string' && getter !== '') {
    return compilePath(getter);
  }
  return compilePath(column.field);
}

export function queryValueReader(
  column: ItpDataGridColumn | undefined,
  field: string,
): ColumnValueReader {
  if (column?.sortPath) {
    return compilePath(column.sortPath);
  }
  return column ? columnValueReader(column) : compilePath(field);
}

export function columnKey(column: ItpDataGridColumn): string {
  return column.id ?? column.field;
}

export function isNumericColumn(column: ItpDataGridColumn): boolean {
  return column.type === 'number';
}

export function columnAlign(
  column: ItpDataGridColumn,
): ItpDataGridColumn['align'] | undefined {
  return column.align ?? (isNumericColumn(column) ? 'right' : undefined);
}

export function alignClass(column: ItpDataGridColumn): string | null {
  const align = columnAlign(column);
  return align ? `text-${align}` : null;
}

export function columnValueLookup(
  columns: readonly ItpDataGridColumn[],
): ColumnValueLookup {
  const readers = new Map<string, ColumnValueReader>();
  for (const column of columns) {
    readers.set(columnKey(column), columnValueReader(column));
  }
  return (row, column) => {
    const read = readers.get(columnKey(column));
    return read ? read(row) : row?.[column.field];
  };
}

export function isCommandColumn(column: ItpDataGridColumn): boolean {
  return column.command ?? column.type === 'command';
}

export function commandAnchorFrom(
  target: EventTarget | null,
): HTMLAnchorElement | null {
  return target instanceof Element ? target.closest('a') : null;
}

export function isCommandOnlyAnchor(anchor: HTMLAnchorElement): boolean {
  const href = anchor.getAttribute('href');
  if (href === null) {
    return true;
  }
  const trimmed = href.trim();
  return trimmed === '' || trimmed === '#';
}

export interface CommandTarget {
  column: ItpDataGridColumn;
  index: number;
  anchor: HTMLAnchorElement;
}

export function resolveCommandTarget(
  target: EventTarget | null,
  columns: readonly ItpDataGridColumn[],
  isCommand: (column: ItpDataGridColumn) => boolean = isCommandColumn,
): CommandTarget | null {
  const anchor = commandAnchorFrom(target);
  if (!anchor) {
    return null;
  }
  const cell = anchor.closest<HTMLElement>('.itp-data-grid__cell');
  const key = cell?.dataset.field;
  const column = key
    ? columns.find((item) => columnKey(item) === key)
    : undefined;
  if (!column || !isCommand(column)) {
    return null;
  }
  const index = Number(
    cell?.closest<HTMLElement>('.itp-data-grid__row')?.dataset.index,
  );
  return Number.isInteger(index) ? { column, index, anchor } : null;
}

export interface AutoFitOptions {
  headerText: string;
  texts: Iterable<string>;
  measure: (text: string) => number;
  padding: number;
  min: number;
  max: number;
  renderedWidth?: number;
}

export const AUTO_FIT_SAMPLE_LIMIT = 20000;

export function autoFitWidth(options: AutoFitOptions): number {
  const { measure, padding, min, max } = options;

  let widest = measure(options.headerText);

  let sampled = 0;
  for (const text of options.texts) {
    if (sampled >= AUTO_FIT_SAMPLE_LIMIT) {
      break;
    }
    sampled += 1;
    if (!text) {
      continue;
    }
    const width = measure(text);
    if (width > widest) {
      widest = width;
    }
  }

  const fitted = Math.max(widest + padding, options.renderedWidth ?? 0);
  return Math.round(Math.min(max, Math.max(min, fitted)));
}

export function createTextMeasurer(
  fontSource: Element | null,
): (text: string) => number {
  const context = document.createElement('canvas').getContext('2d');
  if (!context || !fontSource) {
    return (text: string) => text.length * 7;
  }
  const styles = getComputedStyle(fontSource);
  context.font = `${styles.fontStyle} ${styles.fontWeight} ${styles.fontSize} ${styles.fontFamily}`;
  return (text: string) => context.measureText(text).width;
}

export function horizontalPaddingOf(element: Element | null): number {
  if (!element) {
    return 0;
  }
  const styles = getComputedStyle(element);
  return (
    parseFloat(styles.paddingLeft || '0') +
    parseFloat(styles.paddingRight || '0') +
    parseFloat(styles.borderLeftWidth || '0') +
    parseFloat(styles.borderRightWidth || '0')
  );
}
